// src/pages/admin/Building/Buildings.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FaEye,
    FaEdit,
    FaTrash,
    FaPlus,
    FaSearch,
    FaTimes,
    FaBuilding,
    FaMapMarkerAlt,
    FaMoneyBillWave,
    FaStar,
    FaCheckCircle,
    FaSpinner,
    FaFilter,
    FaImage,
} from 'react-icons/fa';
import { useDispatch } from "react-redux";
import {  FaSyncAlt } from "react-icons/fa";

import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import {
    useGetAdminBuildingsQuery,
    useUpdateBuildingStatusMutation,
    useDeleteBuildingMutation,
} from '../../redux/services/buildingService/building.Services';
import CommonBreadcrumb from '../../common/CommonBreadcrumb';
function Buildings() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [loadingId, setLoadingId] = useState(null);

    // API Hooks
    const { data, isLoading, isError, refetch } = useGetAdminBuildingsQuery({
        page: 1,
        limit: 20,
        type: typeFilter,
        status: statusFilter,
        search: searchTerm,
    });

    const [updateStatus] = useUpdateBuildingStatusMutation();
    const [deleteBuilding] = useDeleteBuildingMutation();

    const buildings = data?.data || [];
    const pagination = data?.pagination || {};
    const totalPages = pagination?.totalPages || 0;
    const totalItems = pagination?.total || 0;

    // Get type label
    const getTypeLabel = (type) => {
        const typeMap = {
            'samaj_bhawan': 'Samaj Bhawan',
            'dharamshala': 'Dharamshala',
            'community_hall': 'Community Hall',
            'marriage_hall': 'Marriage Hall',
        };
        return typeMap[type] || type || 'N/A';
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusMap = {
            'pending': { class: 'badge bg-warning', label: 'Pending' },
            'approved': { class: 'badge bg-success', label: 'Approved' },
            'rejected': { class: 'badge bg-danger', label: 'Rejected' },
        };
        const statusInfo = statusMap[status] || statusMap['pending'];
        return <span className={statusInfo.class}>{statusInfo.label}</span>;
    };

    // Format price
    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(price);
    };

    // Handle status change
    const handleStatusChange = async (id, status) => {
        if (!window.confirm(`Are you sure you want to change status to "${status}"?`)) {
            return;
        }

        try {
            setLoadingId(id);
            await updateStatus({ id, status }).unwrap();
            successToast(`Status updated to ${status}`);
            refetch();
        } catch (error) {
            errorToast(error?.data?.message || 'Failed to update status');
        } finally {
            setLoadingId(null);
        }
    };

    // Handle delete
    const handleDelete = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
            return;
        }

        try {
            setLoadingId(id);
            await deleteBuilding(id).unwrap();
            successToast('Building deleted successfully');
            refetch();
        } catch (error) {
            errorToast(error?.data?.message || 'Failed to delete building');
        } finally {
            setLoadingId(null);
        }
    };

    // Handle search
    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    // Clear filters
    const clearFilters = () => {
        setSearchTerm('');
        setTypeFilter('');
        setStatusFilter('');
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="mt-3">Loading Buildings...</h5>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-5">
                <div className="alert alert-danger">
                    <h5>Something Went Wrong</h5>
                    <p>Unable to load buildings. Please try again.</p>
                    <button className="btn btn-primary" onClick={() => refetch()}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* HEADER */}
            {/* <div className="page-header-breadcrumb all_breadcrumb">
                <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
                    <nav>
                        <h4 className="fw-semibold">
                            🏢 Buildings {totalItems > 0 && <span className="badge-custom bg-primary">{totalItems}</span>}
                        </h4>
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item active">
                                <Link to="/admin">Home</Link>
                            </li>
                            <li className="breadcrumb-item">Buildings</li>
                        </ol>
                    </nav>
                </div>
                <div className="page-title fw-semibold fs-18 mb-0">
                    <button
                        className="btn btn-primary me-2"
                        onClick={() => refetch()}
                    >
                        🔄 Refresh
                    </button>
                    <button
                        className="btn btn-success"
                        onClick={() => navigate("/admin/create-building")}
                    >
                        <FaPlus className="me-1" /> Add Building
                    </button>
                </div>
            </div> */}
            <CommonBreadcrumb
    title="Buildings"
    titleIcon={<FaBuilding className="me-2 text-primary" />}
    count={totalItems}
    breadcrumbs={[
        { label: "Home", link: "/admin" },
        { label: "Buildings" },
    ]}
    buttons={[
        {
            text: "Refresh",
            icon: <FaSyncAlt className="me-1" />,
            onClick: refetch,
            className: "btn btn-primary",
        },
        {
            text: "Add Building",
            icon: <FaPlus className="me-1" />,
            link: "/admin/create-building",
            className: "btn btn-success",
        },
    ]}
/>

            {/* SEARCH AND FILTER SECTION */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-3">
                            <SearchBar
                                search={searchTerm}
                                setSearch={handleSearch}
                                placeholder="Search by name, address..."
                            />
                        </div>

                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                <option value="">All Types</option>
                                <option value="samaj_bhawan">Samaj Bhawan</option>
                                <option value="dharamshala">Dharamshala</option>
                                <option value="community_hall">Community Hall</option>
                                <option value="marriage_hall">Marriage Hall</option>
                            </select>
                        </div>

                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>

                        <div className="col-md-5 text-md-end">
                            {(searchTerm || typeFilter || statusFilter) && (
                                <button
                                    className="btn btn-outline-secondary me-2"
                                    onClick={clearFilters}
                                >
                                    <FaTimes className="me-1" />
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* TABLE */}
            <div className="st-wrapper-card">
                {buildings.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-3">
                            <FaBuilding className="text-muted" size={48} />
                        </div>
                        <h5>No Buildings Found</h5>
                        <p className="text-muted">
                            {searchTerm || typeFilter || statusFilter
                                ? "Try adjusting your search or filter criteria"
                                : "No buildings available"}
                        </p>
                    </div>
                ) : (
                    <div className="st-table-responsive">
                        <table className="table st-custom-table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Building</th>
                                    <th>Type</th>
                                    <th>Location</th>
                                    <th>Capacity</th>
                                    <th>Price/Day</th>
                                    <th>Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buildings.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <div>
                                                <div className="fw-semibold">{item.name}</div>
                                                {item.is_featured && (
                                                    <span className="badge bg-warning text-dark">
                                                        <FaStar className="me-1" /> Featured
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="badge bg-secondary">{getTypeLabel(item.type)}</span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                <FaMapMarkerAlt size={12} className="text-muted" />
                                                <span className="text-muted small">{item.city || 'N/A'}</span>
                                            </div>
                                        </td>
                                        <td>{item.capacity || 'N/A'}</td>
                                        <td>{formatPrice(item.price_per_day)}</td>
                                        <td>
                                            <select
                                                className="form-select form-select-sm"
                                                value={item.status}
                                                onChange={(e) => handleStatusChange(item.id, e.target.value)}
                                                disabled={loadingId === item.id}
                                                style={{ minWidth: '110px' }}
                                            >
                                                <option value="pending">Pending</option>
                                                <option value="approved">Approved</option>
                                                <option value="rejected">Rejected</option>
                                            </select>
                                        </td>
                                        <td className="text-center">
                                            {/* VIEW */}
                                            <button
                                                className="st-action-btn st-view-btn me-1"
                                                onClick={() => navigate(`/admin/view-building/${item.id}`)}
                                                title="View Details"
                                                disabled={loadingId === item.id}
                                            >
                                                <FaEye />
                                            </button>

                                            {/* IMAGES */}
                                            <button
                                                className="st-action-btn st-status-btn me-1"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "building-images",
                                                            title: "Building Images",
                                                            data: item,
                                                        })
                                                    )
                                                }
                                                title="Manage Images"
                                                disabled={loadingId === item.id}
                                            >
                                                <FaImage />
                                            </button>

                                            {/* EDIT */}
                                            <button
                                                className="st-action-btn st-edit-btn me-1"
                                                onClick={() => navigate(`/admin/edit-building/${item.id}`)}
                                                title="Edit"
                                                disabled={loadingId === item.id}
                                            >
                                                <FaEdit />
                                            </button>

                                            {/* DELETE */}
                                            <button
                                                className="st-action-btn st-delete-btn"
                                                onClick={() => handleDelete(item.id, item.name)}
                                                title="Delete"
                                                disabled={loadingId === item.id}
                                            >
                                                {loadingId === item.id ? (
                                                    <span className="spinner-border spinner-border-sm" />
                                                ) : (
                                                    <FaTrash />
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-end mt-4">
                        <Pagination
                            page={1}
                            totalPages={totalPages}
                            onPageChange={(page) => {
                                // Handle page change
                                console.log('Page changed:', page);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default Buildings;